package dev.alimmz.portfolio.data.markdown

import io.ktor.client.HttpClient
import io.ktor.client.plugins.HttpTimeout
import io.ktor.client.request.get
import io.ktor.client.statement.bodyAsText
import kotlinx.serialization.json.*

object GithubMarkdown {
    private val client = HttpClient {
        expectSuccess = true
        install(HttpTimeout) { requestTimeoutMillis = 20000 }
    }
    private val cache = mutableMapOf<String, String>()

    suspend fun load(repositoryUrl: String): String {
        cache[repositoryUrl]?.let { return it }
        val repository = repositoryUrl.removePrefix("https://github.com/").trimEnd('/')
        require(Regex("[A-Za-z0-9_.-]+/[A-Za-z0-9_.-]+").matches(repository))
        // GitHub resolves the README's actual path and default branch, including non-main branches.
        val metadata = Json.parseToJsonElement(client.get("https://api.github.com/repos/$repository/readme").bodyAsText()).jsonObject
        val rawUrl = metadata.getValue("download_url").jsonPrimitive.content
        val sourceUrl = metadata.getValue("html_url").jsonPrimitive.content
        require(rawUrl.startsWith("https://raw.githubusercontent.com/"))
        val markdown = client.get(rawUrl).bodyAsText()
        require(markdown.isNotBlank()) { "The README is empty." }
        return prepareMarkdown(markdown, rawUrl, sourceUrl).also { cache[repositoryUrl] = it }
    }
}

/** Resolve repository-relative paths without assuming a branch or README location. */
fun resolveMarkdownUrl(value: String, base: String, image: Boolean): String {
    val target = value.trim().removeSurrounding("<", ">")
    if (target.startsWith("#")) return if (image) "" else base + target
    if (target.startsWith("//")) return "https:$target"
    if (Regex("^[a-zA-Z][a-zA-Z0-9+.-]*:").containsMatchIn(target)) {
        if (!target.startsWith("https://") && !target.startsWith("http://") && !(target.startsWith("mailto:") && !image)) return ""
        return if (image && target.startsWith("https://github.com/") && "/blob/" in target)
            target.replace("https://github.com/", "https://raw.githubusercontent.com/").replace("/blob/", "/") else target
    }
    val origin = base.substringBefore("://") + "://" + base.substringAfter("://").substringBefore('/')
    val basePath = base.removePrefix(origin).substringBeforeLast('/')
    // Leading slash means repository root, rather than raw.githubusercontent.com's root.
    val segments = basePath.trim('/').split('/').toMutableList()
    if (target.startsWith('/')) {
        val rootSize = if ("raw.githubusercontent.com" in origin) 3 else 4
        while (segments.size > rootSize) segments.removeAt(segments.lastIndex)
    }
    target.trimStart('/').split('/').forEach {
        when (it) {
            ".", "" -> Unit
            ".." -> if (segments.isNotEmpty()) segments.removeAt(segments.lastIndex)
            else -> segments.add(it)
        }
    }
    return origin + "/" + segments.joinToString("/").replace(" ", "%20")
}

fun prepareMarkdown(markdown: String, rawUrl: String, sourceUrl: String): String {
    var fence: String? = null
    return markdown.lines().joinToString("\n") { original ->
        val trimmed = original.trimStart()
        val marker = Regex("^(`{3,}|~{3,})").find(trimmed)?.value
        if (marker != null) {
            if (fence == null) fence = marker else if (marker.first() == fence!!.first() && marker.length >= fence!!.length) fence = null
            original
        } else if (fence != null) original else {
            var line = Regex("<img\\b[^>]*>", RegexOption.IGNORE_CASE).replace(original) { match ->
                fun attribute(name: String) = Regex("$name\\s*=\\s*[\"']([^\"']*)[\"']", RegexOption.IGNORE_CASE).find(match.value)?.groupValues?.get(1).orEmpty()
                val src = resolveMarkdownUrl(attribute("src"), rawUrl, true)
                "![${attribute("alt").replace("]", "")}](<$src>)"
            }
            line = Regex("</?(?:div|p)\\b[^>]*>", RegexOption.IGNORE_CASE).replace(line, "")
            line = Regex("(!?)\\[([^\\]\\n]*)\\]\\((<[^>]*>|[^\\s)]+)([^)]*)\\)").replace(line) { m ->
                val image = m.groupValues[1] == "!"
                val url = resolveMarkdownUrl(m.groupValues[3], if (image) rawUrl else sourceUrl, image)
                "${m.groupValues[1]}[${m.groupValues[2]}](<$url>${m.groupValues[4]})"
            }
            // Reference destinations can serve both images and links. Raw URLs keep files usable.
            Regex("^(\\s*\\[[^\\]]+\\]:\\s*)(<[^>]*>|\\S+)(.*)$").replace(line) { m ->
                m.groupValues[1] + "<" + resolveMarkdownUrl(m.groupValues[2], rawUrl, true) + ">" + m.groupValues[3]
            }
        }
    }
}
